<template>
	<n-notification-provider>
		<n-message-provider>
			<n-dialog-provider>
				<n-config-provider
					preflight-style-disabled
					:theme="naiveTheme"
					:theme-overrides="mergedThemeOverrides">
					<!-- Dev Mode Tenant Selector (Port 5000) -->
					<div
						k-data-id="app-devModeSelector"
						v-if="isDevMode && !devModeInitialized"
						class="flex items-center justify-center h-screen bg-gray-100">
						<div
							k-data-id="app-devModeSelector-container"
							class="bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4">
							<div k-data-id="app-devModeSelector-header" class="mb-6">
								<h1 class="text-2xl font-bold text-gray-800 mb-2">🛠️ Dev Mode</h1>
								<p class="text-gray-600 text-sm">
									Running on port 5000. Enter the tenant domain to load.
								</p>
							</div>
							<div k-data-id="app-devModeSelector-form" class="space-y-4">
								<div>
									<label class="block text-sm font-medium text-gray-700 mb-1">Tenant Domain</label>
									<input
										k-data-id="app-devModeSelector-domainInput"
										v-model="devModeDomain"
										type="text"
										placeholder="e.g., community.example.com"
										class="w-full px-4 py-2 border text-black border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
										@keyup.enter="initDevMode" />
								</div>
								<div
									k-data-id="app-devModeSelector-recentDomains"
									v-if="recentDevDomains.length > 0"
									class="mt-4">
									<label class="block text-sm font-medium text-gray-700 mb-2">Recent Domains</label>
									<div class="space-y-2">
										<button
											v-for="domain in recentDevDomains"
											:key="domain"
											:k-data-id="`app-devModeSelector-recentDomain-${domain}`"
											@click="selectDevDomain(domain)"
											class="text-black w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 truncate">
											{{ domain }}
										</button>
									</div>
								</div>
								<button
									k-data-id="app-devModeSelector-loadBtn"
									@click="initDevMode"
									:disabled="!devModeDomain"
									class="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
									Load Tenant
								</button>
								<p class="text-xs text-gray-400 text-center">
									Go to
									<span class="font-mono">/r</span>
									to switch tenants
								</p>
							</div>
						</div>
					</div>
					<div
						k-data-id="app-container"
						v-if="
							(loaded || (isDevMode && devModeInitialized)) &&
							(!maintenance ||
								(maintenanceMode?.tenants.length !== 0 &&
									!maintenanceMode?.tenants?.includes(tenant?.id)))
						">
						<div
							k-data-id="app-adminView"
							v-if="user?.data?.role?.administrator || user?.data?.role?.collaborator"
							class="flex h-screen w-full">
							<!-- Primary Sidebar - AdminSidebar (leftmost) -->
							<div k-data-id="app-adminSidebar" v-if="!isFullPreview">
								<AdminSidebar v-if="user" @mobile-sidebar-state="isMobileSidebarOpen = $event" />
							</div>

							<!-- Secondary Content Area with MainSideNav and Main Content -->
							<div
								k-data-id="app-mainContainer"
								v-show="!isMobileSidebarOpen"
								class="flex flex-1 min-h-screen relative"
								id="main-app-container">
								<!-- Secondary Sidebar - MainSideNav -->
								<div k-data-id="app-sideNav" v-if="!isFullPreview">
									<MainSideNav v-if="!store.state.hideNav && store.state.sideMenuOpen && user" />
								</div>

								<!-- Main Content Area -->
								<div
									k-data-id="app-contentArea"
									:class="isFullPreview ? 'flex-1' : 'flex-1 overflow-y-auto pt-16 md:pt-0'"
									id="main-app-content">
									<div
										k-data-id="app-routerView"
										:class="
											isFullPreview
												? ''
												: route.name !== 'Edit Page'
													? `w-full h-full align-top p-4 lg:p-6 xl:p-8 ${!store.state.fullWidth ? '2xl:max-w-screen-xl mx-auto' : ''}`
													: ''
										"
										id="router-view">
										<template v-if="!isFullPreview">
											<div k-data-id="app-alert">
												<Alert v-if="store.getters.getAlert" />
											</div>
											<div k-data-id="app-modal">
												<Modal v-if="store.getters.getShowModal" />
											</div>
											<div k-data-id="app-overlay">
												<Overlay v-if="store.getters.getShowOverlay" />
											</div>
											<div k-data-id="app-uploadProgress">
												<UploadProgress />
											</div>
										</template>
										<router-view />
									</div>
								</div>
							</div>
							<div k-data-id="app-debugDrawer">
								<DebugDrawer />
							</div>
							<div k-data-id="app-aiAgentChat">
								<AIAgentChat />
							</div>
						</div>
						<div k-data-id="app-noAccess" v-else>
							<div
								k-data-id="app-noAccess-userMessage"
								v-if="user?.data"
								class="mx-auto text-center mt-32">
								<p>
									You do not have access to this platform. Please contact the administrator of
									{{ tenant?.name }} for assistance.
								</p>
								<p class="mt-4">
									If you believe you are logged into the wrong account
									<span class="text-orange-600 mr-1">{{ user.data.email }}</span>
									<a
										k-data-id="app-noAccess-loginLink"
										href="./login"
										class="text-blue-600 underline"
										@click.prevent="handleLogout">
										Login
									</a>
								</p>
							</div>
							<div k-data-id="app-noUser" v-else-if="user === null">
								<div k-data-id="app-noUser-loginPrompt" v-if="!isLocalhost">
									<LoginPrompt />
								</div>
								<div k-data-id="app-noUser-localRouter" v-else>
									<router-view />
								</div>
							</div>
						</div>
					</div>
					<div
						k-data-id="app-maintenance"
						v-if="
							maintenance &&
							(maintenanceMode?.tenants.length === 0 ||
								maintenanceMode.tenants.includes(tenant?.id))
						">
						<div class="flex items-center justify-center h-screen">
							<div
								k-data-id="app-maintenance-content"
								class="flex flex-col items-center justify-center">
								<img
									k-data-id="app-maintenance-logo"
									v-if="tenant?.branding?.logo"
									:src="tenant?.branding?.logo"
									class="w-32 mb-4" />
								<div k-data-id="app-maintenance-title" class="text-3xl font-bold text-center mb-4">
									{{ tenant?.name }} Maintenance Mode
								</div>
								<div
									k-data-id="app-maintenance-description"
									class="text-center mb-4"
									v-if="maintenanceMode.description">
									{{ maintenanceMode.description }}
								</div>
								<div
									k-data-id="app-maintenance-status"
									class="text-center mb-4"
									v-if="maintenanceMode.status">
									{{ maintenanceMode.status }}
								</div>
							</div>
						</div>
					</div>
				</n-config-provider>
			</n-dialog-provider>
		</n-message-provider>
	</n-notification-provider>
</template>
<script setup>
import { ref, computed, onMounted, provide, watch, onBeforeUnmount } from 'vue'
import { readData, writeData } from '@utils/DataService'
import AdminSidebar from '@root/src/components/Sidebar/AdminSidebar.vue'
import MainSideNav from '@ViewComponents/Shared/MainSideNav.vue'
import Alert from '@ViewComponents/Shared/Popovers/AlertPop.vue'
import Modal from '@ViewComponents/Shared/Popovers/ModalPop.vue'
import Overlay from '@ViewComponents/Shared/Popovers/OverlayPop.vue'
import UploadProgress from '@ViewComponents/Shared/Popovers/UploadProgress.vue'
import DebugDrawer from '@root/src/components/Debug/DebugDrawer.vue'
import AIAgentChat from '@root/src/components/AIAgent/AIAgentChat.vue'
import LoginPrompt from '@root/src/components/LoginPrompt.vue'
import { defaultDb } from '@root/firebaseDatabase'
import { onSnapshot, doc } from 'firebase/firestore'
import { DateTime } from 'luxon'
import { NConfigProvider, NNotificationProvider, NMessageProvider, NDialogProvider } from 'naive-ui'
import Gleap from 'gleap'
import * as Sentry from '@sentry/vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { getAuth, signOut } from 'firebase/auth'
// import { httpsCallable } from 'firebase/functions'
// import { functions } from '@root/firebaseDatabase'
// const notificationsHandler = httpsCallable(functions, 'notificationsHandler')
const store = useStore()
const route = useRoute()
const router = useRouter()

// Full preview mode (for screenshots - hides all UI chrome)
const isFullPreview = computed(() => route.query.fullpreview !== undefined)

// Handle logout when user clicks login link in no-access state
const handleLogout = async () => {
	const auth = getAuth()
	try {
		await signOut(auth)
		router.push('/login')
	} catch (error) {
		console.error('Error logging out:', error)
	}
}

// Mobile sidebar state
const isMobileSidebarOpen = ref(false)

// Dev mode state (port 5000)
const DEV_MODE_PORT = '5000'
const DEV_DOMAINS_STORAGE_KEY = 'komunily_dev_recent_domains'
const DEV_DOMAIN_COOKIE_KEY = 'komunily_dev_domain'
const DEV_AUTH_DOMAIN_COOKIE_KEY = 'komunily_dev_auth_domain' // Tracks which domain user authenticated against
const DEV_AUTH_PORT_COOKIE_KEY = 'komunily_dev_auth_port' // Tracks which port user authenticated on

// Cookie helpers (cookies are shared across ports on same hostname)
const getCookie = (name) => {
	const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
	return match ? decodeURIComponent(match[2]) : null
}

const isDevMode = computed(() => window?.location?.port === DEV_MODE_PORT)
const devModeDomain = ref('')
// Initialize as true if cookie exists (prevents flash of selector)
const devModeInitialized = ref(
	window?.location?.port === DEV_MODE_PORT ? !!getCookie(DEV_DOMAIN_COOKIE_KEY) : false
)
const recentDevDomains = ref([])

// Check if we need to logout due to domain/port change in dev mode
const checkDevModeAuthAndLogout = async (newDomain) => {
	if (!isDevMode.value) return false

	const currentPort = window.location.port
	const authDomain = getCookie(DEV_AUTH_DOMAIN_COOKIE_KEY)
	const authPort = getCookie(DEV_AUTH_PORT_COOKIE_KEY)

	// If domain changed, or this port hasn't authenticated yet for this domain
	if (authDomain && authDomain !== newDomain) {
		// Domain changed - logout required
		console.log('Dev mode: Domain changed, logging out')
		const auth = getAuth()
		await signOut(auth)
		return true
	}

	if (authDomain === newDomain && authPort && authPort !== currentPort) {
		// Same domain but different port hasn't re-authenticated
		console.log('Dev mode: Port not authenticated for this domain, logging out')
		const auth = getAuth()
		await signOut(auth)
		return true
	}

	return false
}

const setCookie = (name, value, days = 30) => {
	const expires = new Date(Date.now() + days * 864e5).toUTCString()
	document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`
}

const deleteCookie = (name) => {
	document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}

// Load recent domains from localStorage on init
const loadRecentDevDomains = () => {
	try {
		const stored = localStorage.getItem(DEV_DOMAINS_STORAGE_KEY)
		if (stored) {
			recentDevDomains.value = JSON.parse(stored)
		}
	} catch (e) {
		console.error('Failed to load recent dev domains:', e)
	}
}

// Save domain to recent list AND cookie (cookie shared across ports)
const saveDevDomain = (domain) => {
	if (!domain) return
	const domains = recentDevDomains.value.filter((d) => d !== domain)
	domains.unshift(domain)
	// Keep only last 5 domains
	recentDevDomains.value = domains.slice(0, 5)
	localStorage.setItem(DEV_DOMAINS_STORAGE_KEY, JSON.stringify(recentDevDomains.value))
	setCookie(DEV_DOMAIN_COOKIE_KEY, domain)
	// Set auth domain - this will trigger logout check on other ports
	setCookie(DEV_AUTH_DOMAIN_COOKIE_KEY, domain)
	// Clear auth port so all ports need to re-authenticate
	deleteCookie(DEV_AUTH_PORT_COOKIE_KEY)
}

// Select a domain from recent list
const selectDevDomain = (domain) => {
	devModeDomain.value = domain
	initDevMode()
}

// Get the effective domain for tenant lookup
const getEffectiveDomain = () => {
	if (isDevMode.value) {
		// Check for cookie first (shared across ports)
		const storedDomain = getCookie(DEV_DOMAIN_COOKIE_KEY)
		if (storedDomain) {
			return storedDomain
		}
		return null // Will trigger dev mode selector
	}
	return window.location.host
}

const loaded = ref(false)
const country = ref(undefined)
const maintenanceMode = ref({
	active: false,
	description: '',
	status: '',
	tenants: []
})
const maintenance = ref(false)
const themeOverrides = ref({
	Carousel: {
		dotColor: '#c0c0c0',
		dotColorActive: '#000000'
	}
})

provide(
	'user',
	computed(() => store.state.userData)
)
provide(
	'tenant',
	computed(() => store.state.tenant)
)

onMounted(() => {
	const xhttp = new XMLHttpRequest()
	xhttp.onreadystatechange = function state() {
		if (this.readyState === 4 && this.status === 200) {
			country.value = JSON.parse(this.responseText).country
			store.commit('setCountry', country.value)
		}
	}
	xhttp.open('GET', 'https://restless-disk-8af0.komunily.workers.dev/', true)
	xhttp.send()
})

const user = computed(() => store.getters.getUserData)
const tenant = computed(() => store.state.tenant)
const meta = computed(() => store.state.meta)
const getSideMenuView = computed(() => store.getters.getSideMenuView)
const getCountry = computed(() => store.getters.getCountry)
const isLocalhost = computed(() => window?.location?.hostname === 'localhost')

// Routes that should show the MainSideNav
const sideNavRoutes = ['Settings', 'Edit Event', 'Events', 'Profile']

const checkSideNav = () => {
	if (sideNavRoutes.includes(route.matched?.[0]?.name)) {
		store.commit('setHideNav', false)
		store.commit('setSideMenuOpen', true)
	} else {
		store.commit('setHideNav', true)
		store.commit('setSideMenuOpen', false)
	}
}

// Theme computed properties
const naiveTheme = computed(() => store.getters['theme/naiveTheme'])
const mergedThemeOverrides = computed(() => {
	const themeSpecificOverrides = store.getters['theme/themeOverrides']
	return {
		...themeOverrides.value,
		...themeSpecificOverrides
	}
})

watch(
	route,
	(val, old) => {
		if (val && old && val.fullPath !== old.fullPath) {
			store.dispatch('resetMeta')
			store.dispatch('resetTab')
			store.dispatch('updateMeta', {
				...meta.value,
				title: `${val.name} - ${tenant.value?.meta?.title || tenant.value?.name || ''}`
			})
			store.dispatch('hideModal')
			store.dispatch('hideOverlay')
		}
		checkSideNav()
	},
	{ immediate: true }
)

const init = () => {
	const timezone = DateTime.local().toFormat('z')

	if (!user.value?.data?.timezone) {
		writeData({
			ref: user.value.ref,
			operation: 'update',
			data: { timezone }
		}).catch((error) => {
			console.error('~ file: App.vue ~ line 225 ~ init ~ error', error)
		})
	}

	if (!user.value?.data?.region) {
		writeData({
			ref: user.value.ref,
			operation: 'update',
			data: { region: getCountry.value }
		}).catch((error) => {
			console.error('~ file: App.vue ~ line 234 ~ init ~ error', error)
		})
	}
	if (!getSideMenuView.value) {
		store.dispatch('updateSideMenuView', 'member')
	}
	if (!Gleap.isUserIdentified() && window.location.hostname !== 'localhost') {
		const data = {
			name: user.value.data.firstName + ' ' + user.value.data.lastName,
			email: user.value.data.email,
			companyId: tenant.value.id,
			companyName: tenant.value.name
		}

		Gleap.identify(user.value.id, data)
	}
}

watch(
	user,
	async (val, old) => {
		if (val && val !== old) {
			init()
			// Mark this port as authenticated for the current domain in dev mode
			if (isDevMode.value && tenant.value?.id) {
				const currentDomain = getCookie(DEV_DOMAIN_COOKIE_KEY)
				if (currentDomain) {
					setCookie(DEV_AUTH_PORT_COOKIE_KEY, window.location.port)
				}
			}
		}
		if (val) {
			Sentry.setUser({ id: user.value.id })
		} else {
			Sentry.setUser(null)
		}
	},
	{ immediate: true }
)
let handleKeyboardShortcut
onMounted(async () => {
	// Initialize theme
	store.dispatch('theme/initTheme')

	// Setup AI Agent keyboard shortcut (Cmd/Ctrl+K)
	handleKeyboardShortcut = (event) => {
		// Check for Cmd (Mac) or Ctrl (Windows/Linux) + K
		if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
			// Only activate in admin areas
			if (user.value?.data?.role?.administrator || user.value?.data?.role?.collaborator) {
				event.preventDefault()
				store.dispatch('aiAgent/toggleAgent')
			}
		}
	}
	window.addEventListener('keydown', handleKeyboardShortcut)

	const snapshots = store.state.unsubscribe
	if (window.location.hostname !== 'localhost') {
		Gleap.initialize('cGv5MGAtNNYcOHjCsnKaIekrd8oZME1p')
	}
	// this uses defaultDb and should not be updated to use dataService which uses dynamicDb
	const maintenanceRef = doc(defaultDb, 'komunily', 'maintenance')
	const unsub = onSnapshot(maintenanceRef, (doc) => {
		const data = doc.data()
		if (data?.description && data?.status) {
			const stateValue = {
				active: true,
				description: data.description,
				status: data.status
			}
			if (data.tenants) {
				stateValue.tenants = data.tenants
			} else {
				stateValue.tenants = []
			}
			maintenanceMode.value = stateValue
			maintenance.value = true
		} else {
			maintenance.value = false
			maintenanceMode.value = {
				active: false,
				description: '',
				status: '',
				tenants: []
			}
		}
		snapshots.push(unsub)
	})

	// Load recent dev domains if in dev mode
	if (isDevMode.value) {
		loadRecentDevDomains()
	}

	// Then handle tenant initialization
	if (!tenant.value) {
		const effectiveDomain = getEffectiveDomain()

		// If in dev mode and no stored domain, wait for user input
		if (isDevMode.value && !effectiveDomain) {
			// Dev mode selector will be shown, don't initialize tenant yet
			return
		}

		initTenantWithDomain(effectiveDomain, snapshots)
	} else {
		loaded.value = true
		if (isDevMode.value) {
			devModeInitialized.value = true
		}
	}
})

// Initialize tenant with a specific domain
const initTenantWithDomain = async (domain, snapshots = store.state.unsubscribe) => {
	// Check if we need to logout due to domain/port mismatch in dev mode
	if (isDevMode.value) {
		await checkDevModeAuthAndLogout(domain)
	}

	const unsubscribe = readData({
		ref: 'tenant',
		filter: {
			where: {
				domains: {
					operator: 'array-contains',
					value: domain
				}
			},
			limit: 1
		},
		listen: true,
		onUpdate: (docs) => {
			if (docs.length > 0) {
				const doc = docs[0]
				store.dispatch('updateTenant', {
					id: doc.id,
					...doc.data,
					ref: doc.ref
				})
				store.dispatch('updateMeta', doc.data.meta)
				store.commit('setUnsubscribe', snapshots)

				// Set favicon if it exists in tenant branding
				if (doc.data?.branding?.favicon) {
					const link = document.querySelector("link[rel~='icon']") || document.createElement('link')
					link.type = doc.data?.branding?.faviconType || 'image/x-icon'
					link.rel = 'icon'
					link.href = doc.data.branding.favicon
					document.head.appendChild(link)
				}

				if (isDevMode.value) {
					devModeInitialized.value = true
				}
			} else {
				console.error('No tenant found for domain:', domain)
				if (isDevMode.value) {
					// Clear the stored domain since it's invalid
					deleteCookie(DEV_DOMAIN_COOKIE_KEY)
					devModeInitialized.value = false
					alert(`No tenant found for domain: ${domain}`)
				}
			}
			loaded.value = true
			snapshots.push(unsubscribe)
		}
	})
}

// Extract hostname from URL or domain string
const extractDomain = (input) => {
	if (!input) return null
	let cleaned = input.trim().toLowerCase()

	// If it looks like a URL (has protocol or starts with www), parse it
	if (cleaned.includes('://') || cleaned.startsWith('www.')) {
		try {
			// Add protocol if missing for URL parsing
			if (!cleaned.includes('://')) {
				cleaned = 'https://' + cleaned
			}
			const url = new URL(cleaned)
			return url.hostname
		} catch {
			// If URL parsing fails, fall through to return as-is
		}
	}

	// Remove any path/query string if present (e.g., "domain.com/path")
	const slashIndex = cleaned.indexOf('/')
	if (slashIndex > 0) {
		cleaned = cleaned.substring(0, slashIndex)
	}

	return cleaned
}

// Dev mode: initialize with user-provided domain
const initDevMode = () => {
	if (!devModeDomain.value) return

	const domain = extractDomain(devModeDomain.value)
	if (!domain) {
		alert('Invalid domain or URL')
		return
	}

	saveDevDomain(domain)
	initTenantWithDomain(domain)
}

onBeforeUnmount(() => {
	window.removeEventListener('keydown', handleKeyboardShortcut)
})
</script>
<style>
.container {
	display: table;
}
.content {
	display: table-cell;
	vertical-align: top;
}
.sidebar {
	display: table-cell;
	vertical-align: top;
}
@media (max-width: 767px) {
	th.font-normal.text-left.pr-16.pl-10 {
		padding: 8px 5px;
	}
	td.pr-16.pl-10.cursor-pointer {
		padding: 8px;
	}
}
</style>
